document.addEventListener('alpine:init', () => {
    Alpine.data('ajaxCart', () => ({
        loading: false,
        async fetchCart() {
            this.loading = true;
            try {
                const [cart, sections] = await Promise.all([
                    fetch(window.Shopify.routes.root + 'cart.js').then((r) => r.json()),
                    fetch(window.Shopify.routes.root + '?sections=cart-drawer').then((r) => r.json()),
                ]);

                const data = {
                    item_count: cart.item_count,
                    sections: sections,
                };
                this.refreshDrawer(data);
            } finally {
                this.loading = false;
            }
        },

        getUpsellRules() {
            // A trigger product can have up to 3 cart_upsell blocks.
            // Each block emits its own <script data-cart-upsell-rule> tag.
            const ruleEls = document.querySelectorAll('script[data-cart-upsell-rule]');
            const rules = [];
            ruleEls.forEach(el => {
                try {
                    const rule = JSON.parse(el.textContent);
                    if (rule && rule.upsell_handle) rules.push(rule);
                } catch (e) {
                    console.warn('Cart upsell: failed to parse rule', e);
                }
            });
            return rules.slice(0, 3);
        },

        // Backward-compat shim — anything that called getUpsellRule() still
        // gets the first rule.
        getUpsellRule() {
            const rules = this.getUpsellRules();
            return rules[0] || null;
        },

        _upsellPropSuffix(index) {
            // First upsell uses bare property names so line items already in
            // existing carts keep working. Additional ones get _2, _3.
            return index === 0 ? '' : '_' + (index + 1);
        },

        attachUpsellRuleProps(formData) {
            const rules = this.getUpsellRules();
            rules.forEach((rule, idx) => {
                const s = this._upsellPropSuffix(idx);
                formData.append('properties[_upsell_handle' + s + ']', rule.upsell_handle);
                formData.append('properties[_upsell_discount_type' + s + ']', rule.discount_type || 'none');
                formData.append('properties[_upsell_discount_value' + s + ']', String(rule.discount_value || 0));
                formData.append('properties[_upsell_label' + s + ']', rule.label || 'You might also like');
                formData.append('properties[_upsell_subtitle' + s + ']', rule.subtitle || '');
                formData.append('properties[_upsell_top_badge' + s + ']', rule.top_badge || '');
                formData.append('properties[_upsell_image_url' + s + ']', rule.upsell_image_url || '');
            });
            return formData;
        },

        attachUpsellRulePropsJson(payload) {
            const rules = this.getUpsellRules();
            if (!rules.length || !payload || !Array.isArray(payload.items)) return payload;
            const upsellProps = {};
            rules.forEach((rule, idx) => {
                const s = this._upsellPropSuffix(idx);
                upsellProps['_upsell_handle' + s] = rule.upsell_handle;
                upsellProps['_upsell_discount_type' + s] = rule.discount_type || 'none';
                upsellProps['_upsell_discount_value' + s] = String(rule.discount_value || 0);
                upsellProps['_upsell_label' + s] = rule.label || 'You might also like';
                upsellProps['_upsell_subtitle' + s] = rule.subtitle || '';
                upsellProps['_upsell_top_badge' + s] = rule.top_badge || '';
                upsellProps['_upsell_image_url' + s] = rule.upsell_image_url || '';
            });
            payload.items = payload.items.map(item => ({
                ...item,
                properties: { ...(item.properties || {}), ...upsellProps },
            }));
            return payload;
        },

        async addItem(payload) {
            this.loading = true;
            try {
                // The cards PDP's bundle box (snippets/pf-cards-guide-bundle.liquid)
                // names the "Complete Pharm Bundle" variant in _pf_bundle_variant.
                // Every add button reads #product-form, so swapping the id here
                // covers the main button, the sticky bar and the floating mobile one.
                if (payload instanceof FormData && payload.get('_pf_bundle_variant')) {
                    payload.set('id', payload.get('_pf_bundle_variant'));
                    payload.delete('_pf_bundle_variant');
                }
                let fetchOptions = { method: 'POST' };
                if (payload instanceof FormData) {
                    payload = this.attachUpsellRuleProps(payload);
                    fetchOptions.body = payload;
                } else {
                    fetchOptions.headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
                    // Items added via the cart-upsell toggle pass _isUpsellToggle:true
                    // so they do NOT carry upsell properties forward. That guarantees
                    // upsells disappear from the drawer the moment the original trigger
                    // product is removed from cart.
                    const isUpsellToggle = payload && payload._isUpsellToggle === true;
                    if (isUpsellToggle) {
                        delete payload._isUpsellToggle;
                    } else {
                        payload = this.attachUpsellRulePropsJson(payload);
                    }
                    fetchOptions.body = JSON.stringify(payload);
                }
                const response = await fetch(window.Shopify.routes.root + 'cart/add.js', fetchOptions);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.description || errorData.message || 'Something went wrong');
                }

                await this.fetchCart();
                if (window.themeSettings.cartDrawerEnabled && window.themeSettings.openDrawerOnAdd) {
                    this.cartOpen = true;
                }
                return await response.json();
            } catch (error) {
                console.error('Error adding to cart:', error);
                throw error.message;
            } finally {
                this.loading = false;
            }
        },

        async updateQuantity(key, qty) {
            this.loading = true;
            let formData = JSON.stringify({
                id: key,
                quantity: qty,
                sections: 'cart-drawer',
            });

            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.description || errorData.message || 'Something went wrong');
                }

                let data = await response.json();
                data = (await this.removeOrphanUpsells(data)) || data;
                this.refreshDrawer(data);
                return data;
            } catch (error) {
                console.error('Error updating cart:', error);
                throw error.message;
            } finally {
                this.loading = false;
            }
        },

        // An upsell added from the drawer is only sold with its trigger product.
        // When the last trigger leaves the cart, take the upsell out with it so
        // it can never check out alone at full price.
        async removeOrphanUpsells(cart) {
            const items = (cart && cart.items) || [];
            const offered = new Set();
            items.forEach((item) => {
                const props = item.properties || {};
                Object.keys(props).forEach((k) => {
                    if (/^_upsell_handle(_\d+)?$/.test(k) && props[k]) offered.add(props[k]);
                });
            });
            const updates = {};
            items.forEach((item) => {
                const props = item.properties || {};
                if (props._upsell_item && !offered.has(item.handle)) updates[item.key] = 0;
            });
            if (!Object.keys(updates).length) return null;
            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ updates: updates, sections: 'cart-drawer' }),
                });
                return response.ok ? await response.json() : null;
            } catch (error) {
                console.error('Error removing upsell:', error);
                return null;
            }
        },

        async removeItem(key) {
            try {
                await this.updateQuantity(key, 0);
            } catch (error) {
                console.error('Error removing item:', error);
            }
        },

        async updateNote(note) {
            this.loading = true;
            let formData = JSON.stringify({
                note: note,
                sections: 'cart-drawer',
            });

            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.description || errorData.message || 'Something went wrong');
                }

                const data = await response.json();
                this.refreshDrawer(data);
                return data;
            } catch (error) {
                console.error('Error updating note:', error);
                throw error.message;
            } finally {
                this.loading = false;
            }
        },

        refreshDrawer(data) {
            // Update the cart count in header if it exists
            const cartCount = data.item_count;
            if (typeof cartCount !== 'undefined') {
                const cartIconBubble = document.querySelector('#cart-icon-bubble');
                if (cartIconBubble) {
                    let badge = cartIconBubble.querySelector('.cart-badge');
                    if (cartCount > 0) {
                        if (!badge) {
                            badge = document.createElement('span');
                            badge.className = 'cart-badge bg-danger';
                            cartIconBubble.appendChild(badge);
                        }
                        badge.textContent = cartCount;
                    } else if (badge) {
                        badge.remove();
                    }
                }
            }

            if (data.sections && data.sections['cart-drawer']) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
                const newDrawer = doc.querySelector('#CartDrawer');
                const currentDrawer = document.querySelector('#CartDrawer');

                if (newDrawer && currentDrawer) {
                    // Replace the inner HTML of the drawer
                    currentDrawer.innerHTML = newDrawer.innerHTML;

                    // Re-initialize Alpine components in the new HTML
                    if (window.Alpine) {
                        window.Alpine.initTree(currentDrawer);
                    }
                }
            }
        },
    }));

    Alpine.data('cartItem', (initialQty, itemKey, inventoryLevel) => ({
        qty: initialQty,
        originalQty: initialQty,
        error: null,
        loading: false,
        timeout: null,
        inventory: inventoryLevel,

        validateQty() {
            if (this.qty < 1) this.qty = 1;
            if (this.qty > this.inventory) this.qty = this.inventory;
        },

        change() {
            this.validateQty();
            this.scheduleUpdate();
        },

        increase() {
            if (this.qty < this.inventory) {
                this.qty++;
                this.scheduleUpdate();
            } else {
                this.error = 'Max available stock reached';
                if (this.errorTimeout) clearTimeout(this.errorTimeout);
                this.errorTimeout = setTimeout(() => {
                    this.error = null;
                }, 3000);
            }
        },

        decrease() {
            if (this.qty > 1) {
                this.qty--;
                this.scheduleUpdate();
            }
        },

        scheduleUpdate() {
            this.error = null;
            if (this.timeout) clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.loading = true;
                this.submit();
            }, 500);
        },

        async submit() {
            try {
                await this.updateQuantity(itemKey, this.qty);
                this.loading = false;
                this.originalQty = this.qty;
                this.error = null;
            } catch (msg) {
                this.loading = false;
                this.qty = this.originalQty;

                let cleanMsg = msg;
                if (msg.toLowerCase().includes('sold out')) cleanMsg = 'Item is sold out';
                if (msg.toLowerCase().includes('cannot add')) cleanMsg = 'Limit reached';

                this.error = cleanMsg;

                // Clear existing timeout if any
                if (this.errorTimeout) clearTimeout(this.errorTimeout);
                this.errorTimeout = setTimeout(() => {
                    this.error = null;
                }, 3000);
            }
        },
    }));
});

// Global interceptor: any anchor pointing at /cart/add?id=... opens the cart drawer
// via AJAX instead of redirecting the user to the /cart page.
document.addEventListener('click', function(e) {
    var link = e.target && e.target.closest && e.target.closest('a[href*="/cart/add"]');
    if (!link) return;
    // Allow modifier-key clicks (open in new tab) to behave natively
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
    var url;
    try { url = new URL(link.getAttribute('href'), window.location.origin); }
    catch (err) { return; }
    var id = url.searchParams.get('id');
    if (!id) return;
    var quantity = parseInt(url.searchParams.get('quantity') || '1', 10) || 1;

    e.preventDefault();

    var cartEl = document.querySelector('[x-data*="ajaxCart"]');
    var alpineData = cartEl && cartEl._x_dataStack && cartEl._x_dataStack[0];
    if (!alpineData || typeof alpineData.addItem !== 'function') {
        // Cart drawer not available — fall back to the original URL
        window.location.href = link.href;
        return;
    }

    Promise.resolve(alpineData.addItem({ items: [{ id: id, quantity: quantity }] }))
        .then(function() { alpineData.cartOpen = true; })
        .catch(function(err) {
            console.error('Cart add failed:', err);
            window.location.href = link.href;
        });
});
